import { Alert, Button, CircularProgress } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import config from '../../config/config';
import { post } from '../../helpers/api.helper';
import { useNicepayScript } from '../../hooks/useNicepayScript';
import { useLoginFormDialogStore } from '../../store/loginFormDialogStore';
import { useVendorStore } from '../../store/vendorStore';
import { NicepayParams, openNicepayAuth } from '../../utils/nicepayAuth';
import { getFittingProducts } from '../../utils/subscriptionApi';

interface CardRegisterButtonProps {
    onSuccess?: (data: any) => void;
    onError?: (error: Error) => void;
    disabled?: boolean;
    variant?: 'contained' | 'outlined' | 'text';
    size?: 'small' | 'medium' | 'large';
    fullWidth?: boolean;
    amt?: number; // 상품 가격 (원)
    planId?: string; // 플랜 ID (standard, premium, business)
    planTitle?: string; // 플랜 이름 (프리미엄, 스탠다드 등)
    existingCardBid?: string; // 기존 카드의 BID (있으면 카드 등록 생략)
    subscriptionPlanId?: string; // 구독 플랜 ID
    businessCode?: string; // 비즈니스 코드
}

interface BillingRegisterResponse {
    MID: string;
    GoodsName: string;
    Amt: number;
    Moid: string;
    EdiDate: string;
    SignData: string;
    ReturnURL: string;
    BillAuthYN: string;
    PayMethod: string;
}

export function CardRegisterButton({
    onSuccess,
    onError,
    disabled = false,
    variant = 'contained',
    size = 'medium',
    fullWidth = false,
    amt = 1000, // 기본값 1000원 (카드 등록용)
    planId = 'default', // 기본값
    planTitle = '기본 플랜', // 기본값
    existingCardBid, // 기존 카드의 BID
    subscriptionPlanId, // 구독 플랜 ID
    businessCode, // 비즈니스 코드
}: CardRegisterButtonProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { vendorId } = useVendorStore();
    const { setIsOpen: openLoginDialog } = useLoginFormDialogStore((state) => state.action);

    // NICEPAY 스크립트 로딩 상태
    const { ready: scriptReady, error: scriptError } = useNicepayScript(
        'https://pg-web.nicepay.co.kr/v3/common/js/nicepay-pgweb.js',
    );

    const handleRegisterCard = async () => {
        try {
            setLoading(true);
            setError(null);

            // 로그인 사용자 필수
            if (!vendorId) {
                setError('로그인이 필요합니다. 로그인 후 다시 시도해주세요.');
                openLoginDialog(true);
                return;
            }

            // 기존 카드가 있으면 바로 approve API 호출
            if (existingCardBid) {
                await handleApproveWithExistingCard();
                return;
            }

            // 백엔드에서 카드 등록을 위한 파라미터 요청
            const registerPayload: any = {
                userId: vendorId,
                goodsName: planTitle,
                amt,
            };

            // 구독 관련 필드 추가 (있는 경우에만)
            if (subscriptionPlanId) {
                registerPayload.subscriptionPlanId = subscriptionPlanId;
            }
            if (businessCode) {
                registerPayload.businessCode = businessCode;
            }

            const response = await post(`${config.api.matching_api}/billing/register`, registerPayload);

            const data: BillingRegisterResponse = response.data;

            if (!data?.MID) {
                throw new Error('등록 파라미터를 받을 수 없습니다.');
            }

            // NICEPAY 인증창 오픈 (팝업으로 열기)
            await openNicepayAuth(data as unknown as NicepayParams, {
                popup: true,
                onSuccess: (result) => {
                    setError(null);
                    onSuccess?.(result);
                },
                onError: (error) => {
                    console.error('카드 등록 실패:', error);
                    const errorMessage = error instanceof Error ? error.message : '카드 등록에 실패했습니다.';
                    setError(errorMessage);
                    onError?.(error instanceof Error ? error : new Error(errorMessage));
                },
            });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.';
            setError(errorMessage);
            onError?.(err instanceof Error ? err : new Error(errorMessage));
        } finally {
            setLoading(false);
        }
    };

    const handleApproveWithExistingCard = async () => {
        try {
            // fittingSubscriptionPlanId 가져오기
            const response = await getFittingProducts();
            const fittingProduct = response.data.find((item) => item.id === planId);

            if (!fittingProduct || !fittingProduct.fittingSubscriptionPlans.length) {
                throw new Error('구독 플랜 정보를 찾을 수 없습니다.');
            }

            const fittingSubscriptionPlanId = fittingProduct.fittingSubscriptionPlans[0].id;

            // approve API 호출
            const approveResponse = await post(`${config.api.matching_api}/billing/approve`, {
                subscriptionPlanId: fittingSubscriptionPlanId,
                userId: vendorId,
                bid: existingCardBid,
                amount: amt,
                goodsName: planTitle,
            });

            const result = approveResponse.data;
            if (result && result.ResultCode === '3001') {
                // 구독 완료 성공 토스트 메시지 표시
                toast.success('구독이 완료되었습니다.', {
                    position: 'top-center',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                // 기존 카드로 구독 완료 시에는 success 플래그와 함께 onSuccess 콜백 호출
                // SubscriptionCheckoutDialog에서 중복 호출을 방지하도록 플래그 전달
                onSuccess?.({ BID: existingCardBid, success: true, alreadyApproved: true });
            } else {
                const errorMsg = result?.ResultMsg || '구독 결제 처리에 실패했습니다.';
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Approve with existing card failed:', error);
            const errorMessage = error instanceof Error ? error.message : '구독 생성에 실패했습니다.';
            setError(errorMessage);
            onError?.(error instanceof Error ? error : new Error(errorMessage));
        }
    };

    const isDisabled = disabled || loading || !scriptReady || !!scriptError;

    return (
        <>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {scriptError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    결제 모듈을 불러올 수 없습니다. 페이지를 새로고침해주세요.
                </Alert>
            )}

            <Button
                variant={variant}
                size={size}
                fullWidth={fullWidth}
                onClick={handleRegisterCard}
                disabled={isDisabled}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{
                    backgroundColor: variant === 'contained' ? '#FF4B6B' : undefined,
                    '&:hover': {
                        backgroundColor: variant === 'contained' ? '#E03E5A' : undefined,
                    },
                    borderRadius: '5px',
                }}
            >
                {loading ? '연결 중...' : '동의 및 구독 결제하기'}
            </Button>
        </>
    );
}
